import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';

interface IVideoPlayer {
  url: string;
  pauseVideoChange: boolean;
  pauseScrollChange?: boolean;
  videoSx?: React.CSSProperties;
  thumbnailUrl?: string;
  fullScreenView?: boolean;
  callBack?: () => void;
}

const CustomVideoPlayer: React.FC<IVideoPlayer> = ({
  url,
  pauseVideoChange,
  pauseScrollChange,
  videoSx,
  thumbnailUrl,
  fullScreenView,
  callBack
}) => {
  const [playing, setPlaying] = useState<boolean>(false);
  const playerRef = useRef<ReactPlayer | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const lastMediaEventTimeRef = useRef<number>(0);
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  const pointerDownPosRef = useRef<{ x: number; y: number } | null>(null);

  const handleVideoPress = () => {
    if (fullScreenView) {
      setPlaying(prev => !prev); 
    }
  };

  useEffect(() => {
    if (fullScreenView && pauseVideoChange) {
      setPlaying(true);
    } else {
      setPlaying(false);
    }
  }, [fullScreenView, pauseVideoChange, pauseScrollChange]);

  useEffect(() => {
    if (fullScreenView) {
      const allVideos = document.querySelectorAll('video');
      const internalPlayer = playerRef.current?.getInternalPlayer() as HTMLVideoElement | null;
      allVideos.forEach(v => {
        if (v !== internalPlayer) {
          v.pause();
        }
      });
    }
  }, [fullScreenView]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMediaEvent = () => {
      lastMediaEventTimeRef.current = Date.now();
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
    };

    const mediaEvents = ['play', 'pause', 'seeking', 'seeked', 'volumechange', 'ratechange'];
    mediaEvents.forEach(eventType => {
      container.addEventListener(eventType, handleMediaEvent, { capture: true });
    });

    return () => {
      mediaEvents.forEach(eventType => {
        container.removeEventListener(eventType, handleMediaEvent, { capture: true });
      });
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent) => {
    isDraggingRef.current = false;
    pointerDownPosRef.current = { x: e.clientX, y: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (pointerDownPosRef.current) {
      const dist = Math.hypot(
        e.clientX - pointerDownPosRef.current.x,
        e.clientY - pointerDownPosRef.current.y
      );
      if (dist > 4) {
        isDraggingRef.current = true;
      }
    }
  };

  const handlePointerUp = () => {
    setTimeout(() => {
      pointerDownPosRef.current = null;
      isDraggingRef.current = false;
    }, 200);
  };

  const isClickOnControls = (event: React.MouseEvent) => {
    const video = containerRef.current?.querySelector('video');
    if (!video) return false;

    const domRect = video.getBoundingClientRect();
    if (!domRect.width || !domRect.height) return false;

    let renderTop = domRect.top;
    let renderBottom = domRect.bottom;
    let renderLeft = domRect.left;
    let renderRight = domRect.right;

    if (video.videoWidth && video.videoHeight) {
      const videoRatio = video.videoWidth / video.videoHeight;
      const domRatio = domRect.width / domRect.height;
      if (domRatio > videoRatio) {
        const renderWidth = domRect.height * videoRatio;
        renderLeft = domRect.left + (domRect.width - renderWidth) / 2;
        renderRight = renderLeft + renderWidth;
      } else {
        const renderHeight = domRect.width / videoRatio;
        renderTop = domRect.top + (domRect.height - renderHeight) / 2;
        renderBottom = renderTop + renderHeight;
      }
    }

    const isWithinVideo = (
      event.clientX >= renderLeft - 10 &&
      event.clientX <= renderRight + 10 &&
      event.clientY >= renderTop &&
      event.clientY <= renderBottom + 10
    );

    return isWithinVideo && event.clientY >= renderBottom - 75;
  };

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (fullScreenView) {
      if (isClickOnControls(event) || isDraggingRef.current) {
        return;
      }
      handleVideoPress();
      return;
    }

    if (isDraggingRef.current) {
      return;
    }

    if (isClickOnControls(event)) {
      return;
    }

    if (Date.now() - lastMediaEventTimeRef.current < 500) {
      return;
    }

    const video = containerRef.current?.querySelector('video');
    const wasPaused = video?.paused;
    const initialTime = video?.currentTime ?? 0;

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    clickTimeoutRef.current = setTimeout(() => {
      clickTimeoutRef.current = null;

      if (Date.now() - lastMediaEventTimeRef.current < 200) {
        return;
      }

      if (video) {
        if (video.paused !== wasPaused) {
          return;
        }
        if (Math.abs(video.currentTime - initialTime) > 0.1) {
          return;
        }
      }

      const allVideos = document.querySelectorAll('video');
      allVideos.forEach(v => {
        v.pause();
      });
      setPlaying(false);
      callBack?.();
    }, 150);
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={handleClick}
      style={{
        width: '100%',
        height: videoSx?.height || '100%',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <ReactPlayer
        ref={playerRef}
        url={url}
        playing={playing}
        light={thumbnailUrl}
        controls
        playsinline
        width='100%'
        height={videoSx?.height || '100%'}
        onClickPreview={(event: any) => {
          setPlaying(true);
          callBack?.();
          event.preventDefault();
          event.stopPropagation();
        }}
        onPause={() => {
          lastMediaEventTimeRef.current = Date.now();
          setPlaying(false);
        }}
        onPlay={() => {
          lastMediaEventTimeRef.current = Date.now();
          setPlaying(true);
        }}
        onSeek={() => {
          lastMediaEventTimeRef.current = Date.now();
        }}
      />
    </div>
  );
};

export default CustomVideoPlayer;
